import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { criarCargoSchema, type AtualizarCargoDto, type CargoResponse, type CriarCargoDto } from '@triade/contracts';
import { useAtualizarCargo, useCargos, useCriarCargo } from '@/api/cargos';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BotaoRelatorios } from '@/relatorios';
import { ApiError } from '@/lib/api';
import { formatarContagem } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const formCargoSchema = z.object({ nome: criarCargoSchema.shape.nome, ativo: z.boolean() });
type FormCargo = z.infer<typeof formCargoSchema>;

/** Selecao na tela: id de um cargo, 'novo' ou nada (so a lista). */
export type SelecaoCargo = string | 'novo' | null;

/** Tela "Cargos" (/cadastros/cargos): lista e formulario de nome e situacao. Leitura com rh.ver; gravar com rh.editar. */
export function CargosPage() {
  const { empresaId } = useSessao();
  const { pode } = usePode();
  const podeEditar = pode('rh.editar');
  const [selecao, setSelecao] = useState<SelecaoCargo>(null);
  const lista = useCargos({ limit: 200 });
  const cargos = lista.data ?? [];
  const pagCargos = usePaginarLista(cargos);
  const aberto = selecao === null ? undefined : cargos.find((c) => c.id === selecao);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os cargos.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os cargos" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando cargos…</Mensagem>;
  else if (cargos.length === 0) {
    corpo = (
      <Mensagem papel="status">
        Nenhum cargo cadastrado.
        {podeEditar && (
          <>
            {' '}
            <button type="button" className="font-medium text-primary hover:underline" onClick={() => setSelecao('novo')}>
              Cadastre o primeiro
            </button>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[320px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Nome</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagCargos.visiveis.map((c) => (
              <tr
                key={c.id}
                data-testid="linha-cargo"
                onClick={() => setSelecao(c.id)}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', selecao === c.id && 'bg-line-soft')}
              >
                <td className="px-4 py-3 font-semibold">{c.nome}</td>
                <td className="px-4 py-3">
                  <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', c.ativo ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
                    {c.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Cargos
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Cargos</h1>
        </div>
      </div>

      <section aria-label="Lista de cargos" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        {corpo}
        <div className="border-t border-border px-4 py-3 text-small text-ink-secondary" data-testid="contagem-cargos">
          {lista.data ? `${formatarContagem(cargos.length)} cargo${cargos.length === 1 ? '' : 's'} na lista` : ' '}
        </div>
      </section>

      {selecao !== null && (selecao === 'novo' || aberto) && (
        <CargoDetalhe key={selecao} cargo={selecao === 'novo' ? undefined : aberto} podeEditar={podeEditar} onFechar={() => setSelecao(null)} onNovo={() => setSelecao('novo')} onCriado={(id) => setSelecao(id)} />
      )}

      {/* Com o registro aberto a RecordToolbar (Salvar) ocupa o rodapé: a barra da lista ficaria por cima. */}
      {selecao === null && (
        <BarraDeAcoesLista
          novo={podeEditar ? { rotulo: 'Novo cargo', onClick: () => setSelecao('novo') } : undefined}
          paginacao={lista.data ? pagCargos.paginacao : undefined}
          relatorios={<BotaoRelatorios tela="cargos" />}
        />
      )}
    </div>
  );
}

function CargoDetalhe({ cargo, podeEditar, onFechar, onNovo, onCriado }: { cargo: CargoResponse | undefined; podeEditar: boolean; onFechar: () => void; onNovo: () => void; onCriado: (id: string) => void }) {
  const criar = useCriarCargo();
  const atualizar = useAtualizarCargo();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const base = useMemo<FormCargo>(() => (cargo ? { nome: cargo.nome, ativo: cargo.ativo } : { nome: '', ativo: true }), [cargo]);
  const form = useForm<FormCargo>({ resolver: zodResolver(formCargoSchema), defaultValues: base, mode: 'onTouched' });
  const { isDirty, dirtyFields, errors } = form.formState;
  const bloqueio = useBlockerDirty(isDirty);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(dirtyFields).length;

  async function gravar(v: FormCargo) {
    setErroSalvar(null);
    try {
      if (cargo) {
        const corpo: AtualizarCargoDto = { nome: v.nome, ativo: v.ativo };
        const gravado = await atualizar.mutateAsync({ id: cargo.id, ...corpo });
        form.reset({ nome: gravado.nome, ativo: gravado.ativo });
        setSalvoEm(new Date());
      } else {
        const corpo: CriarCargoDto = { nome: v.nome };
        const criado = await criar.mutateAsync(corpo);
        form.reset({ nome: criado.nome, ativo: criado.ativo });
        setSalvoEm(new Date());
        onCriado(criado.id);
      }
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  return (
    <>
      <section aria-label="Dados do cargo" className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-body font-semibold">{cargo ? cargo.nome : 'Novo cargo'}</h2>
        {!podeEditar && (
          <p role="status" className="mt-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar os cargos, não alterá-los.
          </p>
        )}
        <fieldset disabled={!podeEditar} className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] items-end gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="cargo-nome" className={ROTULO}>
              Nome do cargo
            </Label>
            <Input id="cargo-nome" maxLength={120} aria-invalid={errors.nome ? true : undefined} aria-describedby={errors.nome ? 'cargo-nome-erro' : undefined} {...form.register('nome')} />
            <MensagemCampo id="cargo-nome-erro" mensagem={errors.nome?.message} />
          </div>
          <label htmlFor="cargo-ativo" className="flex cursor-pointer items-center gap-2">
            <input id="cargo-ativo" type="checkbox" disabled={!cargo} className="size-[18px] accent-primary" {...form.register('ativo')} />
            Ativo
            {!cargo && <span className="text-small text-ink-muted">Todo cargo novo entra ativo.</span>}
          </label>
        </fieldset>
        {erroSalvar && (
          <p role="alert" className="mt-3 text-caption text-danger-ink">
            {erroSalvar}
          </p>
        )}
      </section>

      {podeEditar && (
        <RecordToolbar
          dirty={isDirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!cargo}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? alterados : undefined}
          onList={onFechar}
          onSave={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}
          onNew={onNovo}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </>
  );
}

/** Erro de lista: delega ao ErroFormacao (403 "Sem permissão para ...", 404, 5xx com requestId, rede). */
export function ErroLista({ erro, onTentar, titulo }: { erro: unknown; onTentar?: () => void; titulo: string }) {
  return <ErroFormacao erro={erro} onTentar={onTentar} titulo={titulo} />;
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
